import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import {
  BehaviorSubject,
  catchError,
  first,
  ignoreElements,
  map,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { MessageComponent } from 'src/app/shared/components/message/message.component';
import { MaterialModule } from 'src/app/shared/material/material.module';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { SyncDataService } from 'src/app/core/services/sync-data/sync-data.service';
import { OfflineDomainService } from './services/offline-domain.service';
import { AuthUserService } from 'src/app/core/services/auth-user/auth-user.service';
import { UserRole } from 'src/app/modules/users/constants/users.constants';

@Component({
  selector: 'app-offline',
  standalone: true,
  imports: [CommonModule, MaterialModule, MessageComponent],
  templateUrl: './offline.component.html',
  styleUrl: './offline.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OfflineComponent {
  protected readonly storeLocalDataSubject$ = new Subject<void>();
  protected readonly syncWithServerSubject$ = new Subject<void>();
  protected readonly loadingSubject$ = new BehaviorSubject(false);
  protected readonly storeLocalData$ = this.storeLocalDataSubject$.pipe(
    takeUntilDestroyed(),
    tap(() => this.loadingSubject$.next(true)),
    switchMap(() => this.syncDataService.storeLocalData()),
    switchMap(() => this.offlineDomainService.createOne({ activated: true })),
    tap({
      next: () => {
        const message = `Modo offline activado`;
        this.toastService.success(message);
        this.loadingSubject$.next(false);
        this.close();
      },
      error: () => this.loadingSubject$.next(false),
    })
  );
  protected readonly syncWithServer$ = this.syncWithServerSubject$.pipe(
    takeUntilDestroyed(),
    tap(() => this.loadingSubject$.next(true)),
    switchMap(() => this.syncDataService.calculatePendingToSync()),
    switchMap((metadata) =>
      this.syncDataService
        .syncWithServer()
        .pipe(switchMap(() => this.offlineDomainService.createOne({ activated: false, metadata })))
    ),
    tap({
      next: () => {
        const message = `Modo online activado`;
        this.toastService.success(message);
        this.loadingSubject$.next(false);
        this.close();
      },
      error: () => this.loadingSubject$.next(false),
    })
  );
  protected readonly storeLocalDataError$ = this.storeLocalData$.pipe(
    takeUntilDestroyed(),
    ignoreElements()
  );
  protected readonly syncWithServerError$ = this.syncWithServer$.pipe(
    takeUntilDestroyed(),
    ignoreElements()
  );
  protected readonly pendingToSyncCount$ = this.syncDataService.calculatePendingToSync().pipe(
    first(),
    startWith({
      ordersCreated: 0,
      ordersExecuted: 0,
      ordersDrafted: 0,
    }),
    map(({ ordersCreated, ordersExecuted, ordersDrafted }) => {
      return [
        { text: `Órdenes creadas`, count: ordersCreated },
        { text: `Órdenes borrador`, count: ordersDrafted },
        { text: `Órdenes ejecutadas`, count: ordersExecuted },
      ];
    })
  );
  protected readonly title$ = this.offlineDomainService.activated$.pipe(
    takeUntilDestroyed(),
    map((activated) => (activated ? 'Activar modo online' : 'Activar modo offline'))
  );

  protected readonly vm = Object.freeze({
    activated: this.offlineDomainService.vm.activated,
    loading: toSignal(this.loadingSubject$, { requireSync: true }),
    pendingToSyncCount: toSignal(this.pendingToSyncCount$, { requireSync: true }),
    storeLocalDataError: toSignal(this.storeLocalDataError$),
    syncWithServerError: toSignal(this.syncWithServerError$),
    title: toSignal(this.title$, { requireSync: true }),
  });

  constructor(
    private readonly matDialogRef: MatDialogRef<OfflineComponent>,
    private readonly offlineDomainService: OfflineDomainService,
    private readonly syncDataService: SyncDataService,
    private readonly toastService: ToastService,
    private readonly authUserService: AuthUserService
  ) {}

  close = () => {
    this.matDialogRef.close();
  };

  submit = () => {
    this.offlineDomainService.vm.activated()
      ? this.syncWithServerSubject$.next()
      : this.storeLocalDataSubject$.next();
  };
}
